/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack, Box } from "@mui/material";
import { Method } from "axios";
import { H4 } from "components/generals/_Titles";
import FormikForm from "components/layout/FormikForm";
import BadgeIcon from "@mui/icons-material/Badge";
import { CoreAPI_URLs } from "constants/http.const";
import { Form, FormikProps } from "formik";
import useHttp from "hooks/http.hook";
import { HeaderEnum, InsuranceInterface, PayerInterface } from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { FormProps } from "interfaces";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import FormField from "components/generals/FormInput";
import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import { insuranceFormYUPValidation, validatePhoneInputSymbol } from "utils/form.utils";
import FormPhoneInput from "components/generals/FormPhoneInput";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { useSelector } from "react-redux";
import { selectIsPatientOrDoctor } from "store/slices/auth.slice";

/**
 * Insurance provider information form with automatic payer creation.
 * 
 * Features:
 * - Create and edit insurance provider records
 * - Insurance company details (name, address, contact)
 * - Phone and fax number validation
 * - Country and state information
 * - ZIP code management
 * - Automatic payer creation:
 *   - When new insurance is created, corresponding payer is auto-generated
 *   - Payer inherits contact information from insurance
 * - Form validation with Yup schema
 * - Permission-based access control
 * - Read-only mode for patients and doctors
 * - Success/error message handling
 * 
 * Integration:
 * - Creates insurance record in system
 * - Automatically creates matching payer for billing
 * - Updates insurance list on success
 * 
 * @param model - Insurance data to edit (or new insurance template)
 * @param action - Form action (NEW, MODIFY)
 * @param onCloseAction - Callback when form is closed
 * @param setModelList - Callback to update insurance list
 * @param modelList - Current list of insurance providers
 * @returns Insurance provider form with payer integration
 */
const InsuranceForm: React.FC<FormProps<InsuranceInterface>> = ({
  model,
  action,
  onCloseAction,
  setModelList,
  modelList,
}) => {
  const [isLoading, _error, sendRequest] = useHttp();
  const [isLoadingPayer, _errorPayer, sendRequestPayer] = useHttp();
  const { t } = useTranslation();

  const isReadOnlyUser = useSelector(selectIsPatientOrDoctor);

  const createPayerFromInsurance = (insurance: InsuranceInterface) => {
    const payerData: Partial<PayerInterface> = {
      name: insurance.name,
      lastname: "", 
      country: insurance.country || "",
      state: insurance.state || "",
      age: undefined, 
      phone: insurance.phone ? validatePhoneInputSymbol(insurance.phone) : ""
    };
    sendRequestPayer(
      CoreAPI_URLs.PAYER_GENERAL_OPERATIONS,
      (createdPayer: PayerInterface) => {
        console.log('Payer created successfully from insurance:', createdPayer);
      },
      "POST",
      JSON.stringify(payerData),
      undefined,
      { 
        successMessage: t("insuranceForm.payerCreatedFromInsurance"),
      }
    );
  };

  const submitHandler = (model: Partial<InsuranceInterface>) => {
    model.zip = model.zip?.toString();
    const submitModel = { ...model };
    
    if (submitModel.phone) {
      submitModel.phone = validatePhoneInputSymbol(submitModel.phone);
    }
    
    const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
    const successMessageText = action === FormActionType.NEW ? t("insuranceForm.insuranceCreated") : t("insuranceForm.insuranceUpdated");
    
    sendRequest(
      CoreAPI_URLs.INSURANCE_GENERAL_OPERATION,
      onInsuranceUpdateHandler,
      method,
      JSON.stringify(submitModel),
      [{ header: HeaderEnum.RESOURCE_ID, value: submitModel.id }],
      { successMessage: successMessageText }
    );
  };

  const onInsuranceUpdateHandler = (insurance: InsuranceInterface) => {
    if (action === FormActionType.MODIFY) {
      setModelList(modelList.map((model) => (model.id === insurance.id ? insurance : model)));
    } else {
      if (action === FormActionType.NEW) {
        createPayerFromInsurance(insurance);
      }
      setModelList((oldArray) => [...oldArray, insurance]);
    }
    onCloseAction();
  };

  return (
    <Box sx={{ maxWidth: '70%', mx: 'auto', px: 2 }}>
      <FormikForm
        onSubmit={(model) => {
          submitHandler(model);
        }}
        initialValues={model}
        validationSchema={insuranceFormYUPValidation(t)}
      >
        {({ values, errors, touched, setFieldValue }: FormikProps<Partial<InsuranceInterface>>) => (
          <Form id="insuranceForm" autoComplete="true">
            <Stack alignItems="center" direction="row" gap={2} mb={3}>
              <BadgeIcon fontSize="large" />
              <H4>{t("insuranceForm.title")}</H4>
            </Stack>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={5} lg={5}>
                <FormField
                  type="text"
                  label={t("insuranceForm.name")}
                  name="name"
                  value={values.name || ""}
                  error={errors.name && touched.name}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("insuranceForm.typeScheme")}
                  name="typeScheme"
                  value={values.typeScheme || ""}
                  error={errors.typeScheme && touched.typeScheme}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <FormField
                  type="text"
                  label={t("insuranceForm.payerId")}
                  name="payerId"
                  value={values.payerId || ""}
                  error={errors.payerId && touched.payerId}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={6} mb={3}>
                <FormField
                  type="text"
                  label={t("insuranceForm.email")}
                  name="email"
                  value={values.email || ""}
                  error={errors.email && touched.email}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={6}>
                {isReadOnlyUser ? (
                  <FormField
                    type="text"
                    label={t("insuranceForm.phone")}
                    name="phone"
                    value={values.phone || ""}
                    error={errors.phone && touched.phone}
                    readOnly={isReadOnlyUser}
                    disabled={isReadOnlyUser}
                  />
                ) : (
                  <FormPhoneInput
                    inputClass="phone"
                    specialLabel={t("insuranceForm.phone")}
                    country={"us"}
                    required={true}
                    value={values.phone || ""}
                    error={errors.phone && touched.phone}
                    onChange={(value) => {
                      setFieldValue("phone", value);
                    }}
                  />
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormField
                  type="text"
                  label={t("insuranceForm.address")}
                  name="address"
                  value={values.address || ""}
                  error={errors.address && touched.address}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("insuranceForm.city")}
                  name="city"
                  value={values.city || ""}
                  error={errors.city && touched.city}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("insuranceForm.state")}
                  name="state"
                  value={values.state || ""}
                  error={errors.state && touched.state}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3}>
                <FormField
                  type="text"
                  label={t("insuranceForm.country")}
                  name="country"
                  value={values.country || ""}
                  error={errors.country && touched.country}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12} md={3} lg={3} mb={3}>
                <FormField
                  type="number"
                  label={t("insuranceForm.zip")}
                  name="zip"
                  value={values.zip || ""}
                  error={errors.zip && touched.zip}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} paddingBottom={2} paddingTop={3}>
              <Grid item xs={12}>
                <FormField
                  type="textarea"
                  label={t("insuranceForm.termsPayment")}
                  name="termsPayment"
                  id="termsPayment"
                  multiline
                  rows={6}
                  value={values.termsPayment || ""}
                  error={errors.termsPayment && touched.termsPayment}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
              <Grid item xs={12}>
                <FormField
                  type="textarea"
                  label={t("insuranceForm.requirements")}
                  name="requirements"
                  id="requirements"
                  multiline
                  rows={6}
                  value={values.requirements || ""}
                  error={errors.requirements && touched.requirements}
                  readOnly={isReadOnlyUser}
                  disabled={isReadOnlyUser}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2} alignItems="center" padding={2}>
            {(
              <Grid item xs={12} md={6} lg={6}>
              <PermissionWrapper
                requiredPermissions={[
                  action === FormActionType.NEW
                    ? PermissionsUser[EntityPermissions.INSURANCE].CREATE
                    : PermissionsUser[EntityPermissions.INSURANCE].EDIT,
                ]}
              >
                <Button 
                  type="submit" 
                  variant="contained" 
                  color="info" 
                  disabled={isLoading || isLoadingPayer}
                >
                  {t(
                    action === FormActionType.NEW
                      ? "stepperFormActionButtons.submit"
                      : "stepperFormActionButtons.update"
                  )}
                </Button>
              </PermissionWrapper>
              </Grid>
            )}
            <Grid item xs={12} md={6} lg={6}>
            <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
            </Grid>
            </Grid>
          </Form>
        )}
      </FormikForm>
    </Box>
  );
};

export default InsuranceForm;